import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { wordAudioId, type Beat } from '../content/beats';
import { chapterOf } from '../content/chapters';
import { STORY_LINES } from '../content/storyLines';
import { loadAudio, play, playSequence } from '../audio/player';
import { Sprite } from './Sprite';
import { ComicPanel } from './ComicPanel';
import { BuildWord, type BuildableWord } from './BuildWord';

export interface BeatResultPayload {
  /** Clean catch AND clean rebuild (drives the coin bonus + mastery advance). */
  firstTry: boolean;
  /** Found the broken word with no wrong taps (a gentler "can read this" signal). */
  caught: boolean;
  timeMs: number;
  usedHear: boolean;
}

interface Props {
  beat: Beat;
  /** Progress shown in the comic band. */
  done: number;
  total: number;
  coins: number;
  /** The current mission's title (narrative skin), if any. */
  missionTitle?: string;
  /** Fired when the player taps Next after fixing. `earned` = coins to award. */
  onComplete: (result: BeatResultPayload, earned: number) => void;
}

type Phase = 'find' | 'fix' | 'done';

let introPlayed = false;

function fixWord(beat: Beat): BuildableWord {
  return { boxes: beat.correctGraphemes.map((g) => ({ graphemes: g, phoneme: '', audioId: '' })) };
}

/** One beat: read → catch the Goblin's broken word → rebuild the correct word. Shared
 *  by the session runner (StoryScreen) and placement (Placement). */
export function BeatView({ beat, done, total, coins, missionTitle, onComplete }: Props) {
  const [phase, setPhase] = useState<Phase>('find');
  const [readIndex, setReadIndex] = useState<number | null>(null);
  const [earned, setEarned] = useState(0);
  const cleanCatch = useRef(true);
  const usedHear = useRef(false);
  const startedAt = useRef(Date.now());
  const result = useRef<BeatResultPayload | null>(null);
  const screenRef = useRef<HTMLDivElement>(null);

  // Greet (once) on the first gesture WITHIN the beat — also unlocks the AudioContext.
  // Scoped to the beat's own element so tapping app chrome (the Base / powers overlays,
  // which are sibling layers) never triggers the "find the broken word" narration.
  useEffect(() => {
    void loadAudio();
    const el = screenRef.current;
    if (!el) return;
    const prime = () => {
      if (!introPlayed) {
        introPlayed = true;
        void play(STORY_LINES.intro.id);
      }
    };
    el.addEventListener('pointerdown', prime, { once: true });
    return () => el.removeEventListener('pointerdown', prime);
  }, []);

  // Fresh state + speed timer per beat.
  useEffect(() => {
    setPhase('find');
    setReadIndex(null);
    cleanCatch.current = true;
    usedHear.current = false;
    startedAt.current = Date.now();
    result.current = null;
  }, [beat.id]);

  const hearBeat = () => {
    usedHear.current = true;
    void (async () => {
      await playSequence(beat.words.map(wordAudioId), { gapMs: 110, onClipStart: (i) => setReadIndex(i) });
      setReadIndex(null);
    })();
  };

  const tapWord = (i: number) => {
    if (phase !== 'find') return;
    if (i === beat.brokenIndex) {
      setPhase('fix');
      void (async () => {
        await play(wordAudioId(beat.words[i]));
        await play(STORY_LINES.found.id);
      })();
    } else {
      cleanCatch.current = false;
      void (async () => {
        await play(wordAudioId(beat.words[i]));
        await play(STORY_LINES.wrong.id);
      })();
    }
  };

  const onBuilt = (buildFirstTry: boolean) => {
    const firstTry = cleanCatch.current && buildFirstTry;
    const timeMs = Date.now() - startedAt.current;
    result.current = { firstTry, caught: cleanCatch.current, timeMs, usedHear: usedHear.current };
    const c = 10 + (firstTry ? 5 : 0);
    setEarned(c);
    setPhase('done');
    void (async () => {
      await play(wordAudioId(beat.correctWord));
      await play(STORY_LINES.fixed.id);
    })();
  };

  const next = () => {
    if (result.current) onComplete(result.current, earned);
  };

  const sentence = (
    <div style={signRow}>
      {beat.words.map((w, i) => {
        const isBroken = i === beat.brokenIndex;
        if (phase === 'done' && isBroken) {
          return (
            <motion.span key={i} initial={{ scale: 0.6 }} animate={{ scale: 1 }} style={{ ...chip, ...chipFixed }}>
              {beat.correctWord}
            </motion.span>
          );
        }
        const broke = phase !== 'find' && isBroken;
        const reading = phase !== 'done' && readIndex === i && !broke;
        return (
          <motion.button
            key={i}
            type="button"
            onClick={() => tapWord(i)}
            disabled={phase !== 'find'}
            whileTap={phase === 'find' ? { scale: 0.92 } : undefined}
            animate={broke ? { x: [0, -6, 6, -4, 4, 0] } : { x: 0 }}
            style={{ ...chip, ...(broke ? chipBroken : reading ? chipReading : null) }}
          >
            {w}
          </motion.button>
        );
      })}
    </div>
  );

  const villain = chapterOf(beat.level)?.villain;

  return (
    <div style={screen} ref={screenRef}>
      <ComicPanel
        cracked={phase === 'done'}
        payoffEmoji={beat.payoffEmoji}
        beatId={beat.id}
        done={done}
        total={total}
        missionTitle={missionTitle}
        villainId={villain?.id}
        villainEmoji={villain?.emoji}
      />

      <div style={stage}>
        <div style={heroCol}>
          <Sprite heroId="spidey" kind={phase === 'done' ? 'celebrate' : 'idle'} size={150} />
          <div style={coinPill}>🪙 {coins}</div>
        </div>

        <div style={card}>
          <div style={hint}>
            {phase === 'find' && `🦹 ${villain?.name ?? 'The villain'} broke one word. Tap the word that is wrong.`}
            {phase === 'fix' && '✅ You found it! Sound out the right word and build it.'}
            {phase === 'done' && '🎉 Fixed! On to the next part.'}
          </div>

          {sentence}

          {phase === 'fix' && (
            <div style={buildWrap}>
              <BuildWord
                key={beat.id}
                word={fixWord(beat)}
                onComplete={onBuilt}
                onPlaceCorrect={() => {}}
                onWrong={() => void play(wordAudioId(beat.correctWord))}
                onHintBox={() => void play(wordAudioId(beat.correctWord))}
              />
            </div>
          )}

          <div style={controls}>
            {phase !== 'done' && (
              <button type="button" style={hearBtn} onClick={hearBeat}>
                🔊 Hear it
              </button>
            )}
            {phase === 'done' && (
              <>
                <span style={coinBurst}>+{earned} 🪙</span>
                <button type="button" style={nextBtn} onClick={next}>
                  Next →
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const screen: CSSProperties = { position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--frame)' };
const stage: CSSProperties = { flex: 1, display: 'flex', alignItems: 'center', padding: '12px 28px', gap: 20 };
const heroCol: CSSProperties = { width: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, flexShrink: 0 };
const coinPill: CSSProperties = { padding: '6px 16px', borderRadius: 999, background: 'var(--frame-2)', color: 'var(--gold)', fontWeight: 800, fontSize: 20 };
const card: CSSProperties = {
  flex: 1,
  alignSelf: 'stretch',
  margin: '8px 0',
  background: 'var(--card)',
  borderRadius: 28,
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 22,
  overflow: 'auto',
};
const hint: CSSProperties = { fontSize: 'var(--body-size)', color: 'var(--ink-soft)', fontWeight: 700, textAlign: 'center' };
const signRow: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', alignItems: 'center', maxWidth: 800 };
const buildWrap: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 };
const chip: CSSProperties = {
  fontFamily: 'var(--font-read)',
  fontSize: 'clamp(26px, 5vmin, 48px)',
  fontWeight: 800,
  color: 'var(--ink)',
  background: '#fff',
  borderWidth: 3,
  borderStyle: 'solid',
  borderColor: 'var(--box-border)',
  borderRadius: 14,
  padding: '6px 16px',
  minHeight: 'var(--target-min)',
  cursor: 'pointer',
  lineHeight: 1.1,
};
const chipBroken: CSSProperties = { color: 'var(--spidey-red)', borderColor: 'var(--spidey-red)', background: '#ffecec', textDecoration: 'line-through', cursor: 'default' };
const chipFixed: CSSProperties = { color: '#fff', background: 'var(--good)', borderColor: 'var(--good)' };
const chipReading: CSSProperties = { background: 'var(--box-active-bg)', borderColor: 'var(--box-active-border)', transform: 'translateY(-4px) scale(1.04)' };
const controls: CSSProperties = { display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', justifyContent: 'center' };
const hearBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 22px',
  borderRadius: 999,
  borderWidth: 3,
  borderStyle: 'solid',
  borderColor: 'var(--gold)',
  background: 'transparent',
  color: '#b8860b',
  fontSize: 19,
  fontWeight: 800,
  cursor: 'pointer',
};
const coinBurst: CSSProperties = { fontSize: 26, fontWeight: 800, color: '#b8860b' };
const nextBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 40px',
  borderRadius: 999,
  border: 'none',
  background: 'var(--spidey-red)',
  color: '#fff',
  fontSize: 24,
  fontWeight: 800,
  cursor: 'pointer',
};
